import React, { useState, useEffect } from 'react';
import {
  Typography,
  TextField,
  Box,
  Tabs,
  Tab,
  Grid,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete
} from "@mui/material";
import JsonEditorAce from "../../components/code/ace/json.editor";
import { SchemaVisualRenderer } from "./SchemaVisualRenderer";
import { useDataModelHierarchy } from '../../hooks/useDataModelHierarchy';

const TabPanel = ({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const SchemaEdit: React.FC<{
  item: any;
  onChange: (updatedItem: any) => void;
}> = ({ item, onChange }) => {
  const [updatedItem, setUpdatedItem] = useState(item);
  const [activeTab, setActiveTab] = useState(0);
  const { domains } = useDataModelHierarchy();
  
  // Stable string values for JSON editors (prevents re-renders)
  const [jsonSchemaValue, setJsonSchemaValue] = useState(item?.jsonSchema || '');
  const [editorSchemaValue, setEditorSchemaValue] = useState(item?.editorSchema || '');
  const [listViewConfigValue, setListViewConfigValue] = useState(
    item?.listViewConfig ? JSON.stringify(item.listViewConfig, null, 2) : ''
  );

  // Initialize with defaults if item is empty
  useEffect(() => {
    if (!item || Object.keys(item).length === 0) {
      const defaultSchema = {
        name: '',
        key: '',
        description: '',
        domain: 'identity',
        category: 'entity',
        configuration: {
          isCore: false,
          allowExtensions: true,
          requiresSync: false,
          tags: []
        },
        jsonSchema: JSON.stringify({
          "$schema": "http://json-schema.org/draft-07/schema#",
          "type": "object",
          "title": "New Schema",
          "description": "Schema definition",
          "properties": {
            "id": {
              "type": "string",
              "format": "uuid",
              "description": "Unique identifier"
            }
          },
          "required": ["id"],
          "additionalProperties": false
        }, null, 2)
      };
      setUpdatedItem(defaultSchema);
      onChange(defaultSchema);
    } else {
      setUpdatedItem(item);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item]); // Only re-run when item changes, not when onChange changes

  // Function to handle changes in the form
  const handleChange = (field: string, value: any) => {
    const newItem = { ...updatedItem, [field]: value };
    newItem.updatedAt = new Date().toISOString();
    setUpdatedItem(newItem);
    onChange(newItem); // Call onChange to update the parent component
  };

  const handleNestedChange = (path: string, value: any) => {
    const newItem = { ...updatedItem };
    const pathParts = path.split('.');
    
    let current = newItem;
    for (let i = 0; i < pathParts.length - 1; i++) {
      if (!current[pathParts[i]]) {
        current[pathParts[i]] = {};
      }
      current = current[pathParts[i]];
    }
    current[pathParts[pathParts.length - 1]] = value;
    
    newItem.updatedAt = new Date().toISOString();
    setUpdatedItem(newItem);
    onChange(newItem);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Check if item is not available and return a fallback UI
  if (!updatedItem) {
    return <div>No schema data available</div>;
  }

  return (
    <Box sx={{ padding: "16px" }}>
      <Typography variant="h6" gutterBottom>
        Schema Definition
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Schema Name"
            value={updatedItem.name || ''}
            onChange={(e) => handleChange("name", e.target.value)}
            sx={{ mb: 2 }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Schema Key"
            value={updatedItem.key || ''}
            onChange={(e) => handleChange("key", e.target.value)}
            sx={{ mb: 2 }}
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        multiline
        rows={2}
        label="Description"
        value={updatedItem.description || ''}
        onChange={(e) => handleChange("description", e.target.value)}
        sx={{ mb: 2 }}
      />

      <Autocomplete
        fullWidth
        freeSolo
        options={React.useMemo(() => {
          const dataModelDomains = domains.map(d => d.name);
          const defaultDomains = ['Commerce', 'Identity', 'Location', 'Operations', 'Framework', 'Analytics', 'General'];
          const merged = [...new Set([...dataModelDomains, ...defaultDomains])];
          return merged.sort();
        }, [domains])}
        value={updatedItem.domain || ''}
        onChange={(e, newValue) => handleChange("domain", newValue || '')}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Domain"
            helperText="Select from existing or type new domain"
            sx={{ mb: 2 }}
          />
        )}
      />


      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={updatedItem.configuration?.isCore || false}
              onChange={(e) => handleNestedChange("configuration.isCore", e.target.checked)}
            />
          }
          label="Core Schema"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={updatedItem.configuration?.allowExtensions ?? true}
              onChange={(e) => handleNestedChange("configuration.allowExtensions", e.target.checked)}
            />
          }
          label="Allow Extensions"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={updatedItem.configuration?.requiresSync || false}
              onChange={(e) => handleNestedChange("configuration.requiresSync", e.target.checked)}
            />
          }
          label="Requires Sync"
        />
      </Box>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Entity Schema" />
        <Tab label="Editor Schema" />
        <Tab label="List View" />
        <Tab label="Visual Preview" />
      </Tabs>

      {/* Entity Schema Tab (Full JSON Schema) */}
      <TabPanel value={activeTab} index={0}>
        <Typography variant="body1" gutterBottom>
          JSON Schema Definition
        </Typography>
        <Box sx={{ height: '400px' }}>
          <JsonEditorAce
            value={jsonSchemaValue}
            onChange={(value) => {
              setJsonSchemaValue(value);  // Update stable value
              // JsonEditorAce now always passes string values
              const jsonString = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
              handleChange("jsonSchema", jsonString);
              
              // Try to auto-populate if fields are empty and JSON looks complete
              if ((!updatedItem.name || !updatedItem.key) && jsonString.trim()) {
                try {
                  // Only try to parse if it looks like complete JSON
                  if (jsonString.includes('"title"') && jsonString.trim().endsWith('}')) {
                    const parsed = JSON.parse(jsonString);
                    
                    // Batch all auto-population updates into a single state update
                    const updates: any = {};
                    
                    if (!updatedItem.name && parsed.title) {
                      updates.name = parsed.title;
                    }
                    
                    if (!updatedItem.key && parsed.title) {
                      const kebabKey = parsed.title
                        .toLowerCase()
                        .replace(/[^a-z0-9]+/g, '-')
                        .replace(/^-+|-+$/g, '');
                      updates.key = kebabKey;
                    }
                    
                    if (!updatedItem.description && parsed.description) {
                      updates.description = parsed.description;
                    }
                    
                    // Apply all updates at once if we have any
                    if (Object.keys(updates).length > 0) {
                      const newItem = { ...updatedItem, ...updates };
                      newItem.updatedAt = new Date().toISOString();
                      setUpdatedItem(newItem);
                      onChange(newItem);
                    }
                  }
                } catch (error) {
                  // Ignore parsing errors during editing - they're expected
                }
              }
            }}
          />
        </Box>
      </TabPanel>

      {/* Editor Schema Tab (JSON Schema for UI forms) */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="body1" gutterBottom>
          Editor Schema (Defines the entity editor UI)
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          This JSON Schema controls which fields appear in Create/Edit forms, their validation, and UI widgets.
          Framework fields (context, derivedBy, _id, etc.) are automatically excluded.
        </Typography>
        <Box sx={{ height: '400px' }}>
          <JsonEditorAce
            value={updatedItem.editorSchema || ''}
            onChange={(value) => {
              const jsonString = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
              handleChange("editorSchema", jsonString);
            }}
          />
        </Box>
      </TabPanel>

      {/* List View Configuration Tab */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="body1" gutterBottom>
          List View Configuration
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Configure which columns appear in the list view, default sorting, and filters.
          If not configured, columns will be auto-selected from the schema.
        </Typography>
        <Box sx={{ height: '400px' }}>
          <JsonEditorAce
            value={updatedItem.listViewConfig ? JSON.stringify(updatedItem.listViewConfig, null, 2) : JSON.stringify({
              columns: ['id', 'name', 'status', 'updatedAt'],
              defaultSort: { field: 'updatedAt', order: 'DESC' },
              filters: ['status'],
              searchFields: ['name', 'email']
            }, null, 2)}
            onChange={(value) => {
              const jsonString = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
              try {
                const parsed = JSON.parse(jsonString);
                handleChange("listViewConfig", parsed);
              } catch (e) {
                // Invalid JSON, don't update yet
              }
            }}
          />
        </Box>
      </TabPanel>

      {/* Visual Preview Tab */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="body1" gutterBottom>
          Schema Visualization
        </Typography>
        <SchemaVisualRenderer 
          jsonSchema={updatedItem.jsonSchema}
        />
      </TabPanel>
    </Box>
  );
};

export default SchemaEdit;
